import { getDatabase, ref, set, push } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { ComponentDocument } from '../types/protocols';
import { softDeleteFile } from './fileDeletionApi';
import { recordActivity } from './activityApi';

class ComponentDocumentsApi {
    private db = getDatabase();
    private storage = getStorage();

    private getStoragePath(buildingKey: string, componentKey: string, storageFilename: string): string {
        return `buildings/${buildingKey}/structure/components/${componentKey}/documents/${storageFilename}`;
    }

    private getDbPath(buildingKey: string, componentKey: string, docKey: string): string {
        return `buildings/${buildingKey}/structure/components/${componentKey}/documents/${docKey}`;
    }

    async uploadDocument(
        buildingKey: string,
        componentKey: string,
        file: File
    ): Promise<ComponentDocument> {
        const user = getAuth().currentUser;
        if (!user) throw new Error('Du måste vara inloggad');

        if (file.type !== 'application/pdf') {
            throw new Error('Endast PDF-filer är tillåtna');
        }

        // Firebase auto-ID keeps document keys consistent with the rest of the
        // schema (iOS uses childByAutoId for the same path).
        const documentsPath = `buildings/${buildingKey}/structure/components/${componentKey}/documents`;
        const docKey = push(ref(this.db, documentsPath)).key!;
        const storageFilename = `${file.name.replace(/\.pdf$/i, '')}_${docKey.substring(0, 6)}.pdf`;
        const storagePath = this.getStoragePath(buildingKey, componentKey, storageFilename);

        // Upload to storage
        const fileRef = storageRef(this.storage, storagePath);
        await uploadBytes(fileRef, file);

        // Create document metadata
        const document: ComponentDocument = {
            key: docKey,
            name: file.name.replace(/\.pdf$/i, ''),
            storageFilename,
            fileSize: file.size,
            uploadedAt: formatDateForStorage(new Date()),
            uploadedBy: user.uid
        };

        // Save to database
        const docRef = ref(this.db, this.getDbPath(buildingKey, componentKey, docKey));
        await set(docRef, document);
        recordActivity('document.uploaded', { buildingKey, targetKey: docKey });

        return document;
    }

    /**
     * Delete a component document (soft delete — moves the metadata node to a
     * tombstone at deletedFiles/{key}; the Storage blob is left for a future
     * cleanup job)
     */
    async deleteDocument(
        buildingKey: string,
        componentKey: string,
        docKey: string,
        storageFilename: string
    ): Promise<void> {
        await softDeleteFile({
            originalPath: this.getDbPath(buildingKey, componentKey, docKey),
            entityType: 'componentDocument',
            // Component documents store storageFilename, not storagePath
            resolveStoragePath: () => this.getStoragePath(buildingKey, componentKey, storageFilename),
        });
    }

    async getDocumentUrl(
        buildingKey: string,
        componentKey: string,
        storageFilename: string
    ): Promise<string> {
        const storagePath = this.getStoragePath(buildingKey, componentKey, storageFilename);
        const fileRef = storageRef(this.storage, storagePath);
        return getDownloadURL(fileRef);
    }
}

export const componentDocumentsApi = new ComponentDocumentsApi();
